import { useTranslation } from "react-i18next";
import { DataTable } from "../components/table/DataTable";
import { useState, useCallback } from "react";
import { fetchTopicCategoryService } from "../../providers/service/fetchTopicCategoryService";
import {
  emptyTopicCategory,
  TopicCategory,
  TopicCategoryFormData,
} from "../../core/topicCategory/entities/TopicCategory";
import { useTopicCategoriesStore } from "../../core/store/topicCategoryStore";
import topicCategoryTableConfig from "./config/topicCategory/topicCategoryTableConfig";
import Form from "../components/form/Form";
import { Modal } from "../components/modal/Modal";
import { useTopicCategoryFormValidation } from "../../core/hooks/form/useTopicCategoryFormValidation";
import topicCategoryFormConfig from "./config/topicCategory/topicCategoryFormConfig";
import { useQuery } from "@tanstack/react-query";
import { deleteTopicCategoryUseCase } from "../../core/topicCategory/useCases/deleteTopicCategoryUseCase";
import { addUpdateTopicCategoryUseCase } from "../../core/topicCategory/useCases/addUpdateTopicCategoryUseCase";
import { updateTopicCategoryOrderUseCase } from "../../core/topicCategory/useCases/updateTopicCategoryOrderUseCase";
import { useTrainingRoute } from "../../core/hooks/useTrainingRoute";
import { useAdminPermissions } from "../../core/hooks/useAdminPermissions";
import PageLayout from "../components/layout/PageLayout";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { trainingId } = useTrainingRoute();
  const permissions = useAdminPermissions();
  const { topicCategories } = useTopicCategoriesStore();
  const [selected, setSelected] = useState<TopicCategory | null>(null);
  const form = useTopicCategoryFormValidation(selected, t);

  const { refetch } = useQuery({
    queryKey: ["topicCategory", trainingId],
    queryFn: async () => {
      await fetchTopicCategoryService(trainingId as string);
      return true;
    },
    enabled: !!trainingId,
  });

  const handleReorder = useCallback(
    async (list: TopicCategory[]) => {
      const success = await updateTopicCategoryOrderUseCase.exec(list);
      if (success) refetch();
    },
    [refetch]
  );

  return (
    <>
      <PageLayout isLoading={false}>
        <DataTable
          title={t("topicCategory.title")}
          datas={topicCategories || []}
          columns={topicCategoryTableConfig}
          filters={["name"]}
          onRowClicked={(row: TopicCategory) => {
            setSelected(row);
          }}
          onAdd={
            permissions.canCreate
              ? () =>
                  setSelected(
                    emptyTopicCategory(trainingId as string) as TopicCategory,
                  )
              : undefined
          }
          onReorder={permissions.canReorder ? handleReorder : undefined}
        />
      </PageLayout>

      <Modal
        isOpen={!!selected}
        onClose={() => {
          setSelected(null);
        }}
        title={selected?.id ? t("form.edit") : t("form.add")}
      >
        <Form
          className="md:min-w-[350px]"
          id={selected?.id}
          onSave={async (
            data: TopicCategoryFormData,
            id: string | number | null
          ) => {
            const success = await addUpdateTopicCategoryUseCase.exec(
              data as TopicCategoryFormData,
              id
            );
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          onDelete={async (id: string | number) => {
            const success = await deleteTopicCategoryUseCase.exec(id);
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          form={form}
          formConfig={topicCategoryFormConfig}
          onCancel={() => setSelected(null)}
        />
      </Modal>
    </>
  );
}
